import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { Branch } from "../../types/user.type";
import { ALL } from "../CreditDebitNote/constants";
import { DOCUMENT_SEARCH_MAX_LENGTH } from "./constants";
import { DOCUMENT_STATUS_FILTER, DocumentListFilterValue } from "./documentListFilter";

interface ListFilterProps {
  canSelectBranch: boolean;
  branches: Branch[];
  value: DocumentListFilterValue;
  onChange: (patch: Partial<DocumentListFilterValue>) => void;
}

const searchSlotProps = {
  htmlInput: { maxLength: DOCUMENT_SEARCH_MAX_LENGTH },
  input: {
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon fontSize="small" />
      </InputAdornment>
    ),
  },
};

const dateSlotProps = {
  textField: { size: "small" as const, fullWidth: true },
  field: { clearable: true },
};

/** The filters of an Express document list: branch (head office), date range, status and search. */
export default function ListFilter({
  canSelectBranch,
  branches,
  value,
  onChange,
}: Readonly<ListFilterProps>) {
  const handleBranchChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ branchCode: event.target.value });
  const handleStatusChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({
      status: DOCUMENT_STATUS_FILTER.find((option) => option.value === event.target.value)?.value ?? ALL,
    });
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ search: event.target.value });
  const handleDateFromChange = (date: Dayjs | null) => onChange({ dateFrom: date });
  const handleDateToChange = (date: Dayjs | null) => onChange({ dateTo: date });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <Grid container spacing={2}>
        {canSelectBranch && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              size="small"
              fullWidth
              label="สาขา"
              value={value.branchCode}
              onChange={handleBranchChange}
            >
              <MenuItem value={ALL}>ทั้งหมด</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.uuid} value={branch.branch_code}>
                  {branch.branch_code} - {branch.branch_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: canSelectBranch ? 2 : 3 }}>
          <DatePicker
            label="วันที่ ตั้งแต่"
            value={value.dateFrom}
            format="DD/MM/YYYY"
            onChange={handleDateFromChange}
            maxDate={value.dateTo ?? undefined}
            slotProps={dateSlotProps}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: canSelectBranch ? 2 : 3 }}>
          <DatePicker
            label="ถึง"
            value={value.dateTo}
            format="DD/MM/YYYY"
            onChange={handleDateToChange}
            minDate={value.dateFrom ?? undefined}
            slotProps={dateSlotProps}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="สถานะ"
            value={value.status}
            onChange={handleStatusChange}
          >
            {DOCUMENT_STATUS_FILTER.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: canSelectBranch ? 3 : 4 }}>
          <TextField
            size="small"
            fullWidth
            label="ค้นหา"
            placeholder="เลขที่เอกสาร / รหัสหรือชื่อ"
            value={value.search}
            onChange={handleSearchChange}
            slotProps={searchSlotProps}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}
